import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';

import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import ErrorView from '../../../../components/global/ErrorView';
import {
  AlbumForContributorShare,
  AlbumFolderForContributorShare,
} from '../../../../graphql/queries/contributors';
import AllPhotosAccessLinkHandlerHeadMeta from './AllPhotosAccessLinkHandlerHeadMeta';
import { HierarchyAllPhotosAccessViewCreate } from '../../../../graphql/mutations/hierarchy-groups';
import { getErrorString } from '../../../../util/normalize-errors';
import { isAlbum } from '../../../../util/album';
import {
  WEB_GALLERY_PRO_ONBOARDING_MODAL,
  WebGalleryProOnboardingInterstitialModal,
} from '../../../web-gallery/views/WebGalleryProOnboardingInterstitial';
import { ALL_PHOTOS_MODE } from '../../../../constants/album-folder';
import useOpenModal from '../../../../hooks/useOpenModal';
import useCloseModal from '../../../../hooks/useCloseModal';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingAlbum: PropTypes.bool,
  loadingAlbumFolder: PropTypes.bool,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  hierarchyAllPhotosAccessViewCreate: PropTypes.func.isRequired,
};

const AllPhotosAccessLinkHandler = ({
  album,
  albumFolder,
  loadingAlbum,
  loadingAlbumFolder,
  hierarchyAllPhotosAccessViewCreate,
  history,
}) => {
  const entity = album || albumFolder;
  const [error, setError] = useState('');
  const openModal = useOpenModal();
  const closeModal = useCloseModal();

  const allPhotosAccessCreate = async () => {
    if (!entity) {
      return;
    }

    try {
      // As it currently stands, all photos access mode can only be enabled
      // at the albumFolder level. However, since it is possible users are
      // redirected to this route via consumer/matches/:albumId/all, we need
      // to ensure they're directed back to the same place. Therefore, we
      // add the All Photos access at the albumFolder level while still
      // redirecting to the original entity (the album).
      //
      // DO NOTE This could change at any point if we decide to have granular
      // control over all photos access. At such state, only the else condition
      // should matter.
      if (isAlbum(entity)) {
        await hierarchyAllPhotosAccessViewCreate(
          entity?.root_album_folder?.id ?? entity.id,
        );
      } else {
        await hierarchyAllPhotosAccessViewCreate(entity.id);
      }

      closeModal(WEB_GALLERY_PRO_ONBOARDING_MODAL);
      history.replace(`/consumer/matches/${entity?.id}/all`);
    } catch (err) {
      setError(getErrorString(err));
    }
  };

  useEffect(() => {
    if (
      albumFolder &&
      albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC &&
      albumFolder?.joins_enabled
    ) {
      openModal(WEB_GALLERY_PRO_ONBOARDING_MODAL);
    } else if (entity) {
      allPhotosAccessCreate();
    }
  }, [albumFolder, entity]);

  if (loadingAlbum || loadingAlbumFolder) {
    return <FullScreenLoader />;
  }

  if (!loadingAlbum && !loadingAlbumFolder && !entity) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'It looks like the link provided does not match any of our records.',
          },
        }}
      />
    );
  }

  if (error) {
    return (
      <ErrorView
        location={{
          state: {
            error,
          },
        }}
      />
    );
  }

  return (
    <Fragment>
      <AllPhotosAccessLinkHandlerHeadMeta entity={entity} />
      <FullScreenLoader />
      <WebGalleryProOnboardingInterstitialModal
        onCloseAction={allPhotosAccessCreate}
        albumFolderId={albumFolder?.id}
      />
    </Fragment>
  );
};

AllPhotosAccessLinkHandler.propTypes = propTypes;

export { AllPhotosAccessLinkHandler as AllPhotosAccessLinkHandlerComp };

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ match }) => ({
    variables: {
      id: match.params.entityId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    loadingAlbum: loading,
  }),
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.entityId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loadingAlbumFolder: loading,
    }),
  },
);

const withHierarchyAllPhotosAccessViewCreate = graphql(
  HierarchyAllPhotosAccessViewCreate,
  {
    props: ({ mutate }) => ({
      hierarchyAllPhotosAccessViewCreate: hierarchyItemId =>
        mutate({
          variables: {
            input: {
              hierarchy_item_id: hierarchyItemId,
            },
          },
        }),
    }),
  },
);

const mapStateToProps = ({ auth }) => {
  return {
    authenticated: auth.authenticated,
  };
};

const mapDispatchToProps = {};

export default compose(
  withRouter,
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
  withHierarchyAllPhotosAccessViewCreate,
  connect(mapStateToProps, mapDispatchToProps),
)(AllPhotosAccessLinkHandler);
